import { useEffect, useRef } from 'react';

/**
 * iBoon Identity's own topbar, loaded from identity.
 *
 * Not a copy. Rule 1 of identity's integration rulebook is that no
 * application grows its own account menu or sign-in button, and the reason is
 * visible in the estate: eight apps each deciding for themselves what "signed
 * in" looks like is eight places for it to be subtly different, and eight
 * places to change when it moves.
 *
 * The contract below was read from the live script rather than copied from
 * another app's integration — the widget has been rewritten at least once,
 * and an older app's attributes may already be naming things that no longer
 * exist. What it actually reads today is `data-target`, `data-signed-in`,
 * `data-sign-in-url`, `data-sign-out-url` and `data-handles-auth`.
 *
 * `data-handles-auth` matters here. This application does its own redirect to
 * identity rather than handing the widget URLs to navigate to, and telling it
 * so is what stops a console warning on every page load about URLs it is
 * right not to have.
 */
export function Topbar({ signedIn }: { signedIn: boolean }) {
  const mount = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const target = mount.current;
    if (!target) return;

    const script = document.createElement('script');
    script.src = `${import.meta.env.VITE_IDENTITY_URL ?? 'https://identity.iboon.in'}/widget/topbar.js`;
    script.async = true;
    script.dataset.target = '#iboon-topbar';
    script.dataset.signedIn = String(signedIn);
    script.dataset.handlesAuth = 'true';
    document.head.appendChild(script);

    return () => {
      script.remove();
      // The widget renders into the mount rather than owning it, so clearing
      // it is enough; leaving the old markup would double it on a re-render.
      target.innerHTML = '';
    };
  }, [signedIn]);

  return <div id="iboon-topbar" ref={mount} />;
}
